import { type ButtonProps, Skeleton } from "@mui/material";
import { ProductType } from "@repo/api";
import { readEnv } from "@repo/helpers";
import { LocalStorageKeys, type PensionContractType } from "@repo/logic";
import { ChangePensionContribution, Icons, PensionQuestionnaireStepperButton, useTypedTranslation } from "@repo/ui";
import { type FC, useCallback } from "react";
import { type ISmartAction, SmartAction } from ".";
import { useContractId } from "../../../context/ContractIDContext";
import { useContractTypeContext } from "../../../context/ContractTypeContext";
import { AMLRedirect } from "../../AML";
import { getBankIdAuthorizationUri } from "../../AML/identification";

export const Changes: FC<ISmartAction> = (props) => {
    const { t } = useTypedTranslation();
    return (
        <SmartAction
            {...props}
            action="Changes"
            icon={<Icons.TaxCertificate sx={{ width: "2.4rem", height: "2.4rem" }} />}
        >
            {t("SmartActionListType.Changes")}
        </SmartAction>
    );
};

export const InvestmentStrategy: FC<ISmartAction> = (props) => {
    const { t } = useTypedTranslation();
    return (
        <SmartAction
            {...props}
            action="InvestmentStrategy"
            icon={<Icons.Graph sx={{ width: "2.4rem", height: "2.4rem" }} />}
        >
            {t("SmartActionListType.InvestmentStrategy")}
        </SmartAction>
    );
};

export const NextPayment: FC<ISmartAction> = (props) => {
    const { t } = useTypedTranslation();
    return (
        <SmartAction {...props} action="NextPayment" icon={<Icons.Pig sx={{ width: "2.4rem", height: "2.4rem" }} />}>
            {t("SmartActionListType.NextPayment")}
        </SmartAction>
    );
};

export const Payments: FC<ISmartAction> = (props) => {
    const { t } = useTypedTranslation();
    return (
        <SmartAction {...props} action="Payments" icon={<Icons.Pig sx={{ width: "2.4rem", height: "2.4rem" }} />}>
            {t("SmartActionListType.Payments")}
        </SmartAction>
    );
};

export const Report: FC<ISmartAction> = (props) => {
    const { t } = useTypedTranslation();
    return (
        <SmartAction
            {...props}
            action="Report"
            icon={<Icons.LightningUmbrella sx={{ width: "2.4rem", height: "2.4rem" }} />}
        >
            {t("SmartActionsButtons.Report")}
        </SmartAction>
    );
};

export const Fond: FC<ISmartAction> = (props) => {
    const { t } = useTypedTranslation();
    const { contractId } = props;

    const { contractType } = useContractTypeContext();

    const handleBeforeBankIdAuthorization = useCallback(() => {
        localStorage.setItem(
            LocalStorageKeys.BANK_ID_LAST_ACTION,
            JSON.stringify({
                redirect: AMLRedirect.ContributionWithQuestionare,
                contractId,
                contractType: contractType as PensionContractType,
            }),
        );
    }, [contractId, contractType]);

    if (
        readEnv("GATSBY_FEATURE_IS_NEW_DPS_CONTRIBUTION_CHANGE_ENABLED") === "true" &&
        contractType === ProductType.Uf
    ) {
        return (
            <ChangePensionContribution
                disabled={props.disabled}
                kind="homePage"
                icon={<Icons.Pig sx={{ width: "2.4rem", height: "2.4rem" }} />}
                bankIdAuthorizationUri={getBankIdAuthorizationUri()}
                contractId={contractId}
                contractType={contractType as PensionContractType}
                beforeBankIdAuthorizationAction={handleBeforeBankIdAuthorization}
                scrollToEmployerSection={false}
                buttonText={t("investmentStrategy.changePensionContribution.changeTextLong")}
            />
        );
    }
    return (
        <SmartAction {...props} action="Fond" icon={<Icons.Pig sx={{ width: "2.4rem", height: "2.4rem" }} />}>
            {t("SmartActionsButtons.ChangeContribution")}
        </SmartAction>
    );
};

export const TaxCertificate: FC<ISmartAction> = (props) => {
    const { t } = useTypedTranslation();
    return (
        <SmartAction
            {...props}
            action="TaxCertificate"
            icon={<Icons.TaxCertificate sx={{ width: "2.4rem", height: "2.4rem" }} />}
        >
            {t("SmartActionListType.TaxCertificate")}
        </SmartAction>
    );
};

export const EmployerContribution: FC<ISmartAction> = (props) => {
    const { t } = useTypedTranslation();
    const { contractId } = props;
    const { contractType } = useContractTypeContext();

    const handleBeforeBankIdAuthorization = useCallback(() => {
        localStorage.setItem(
            LocalStorageKeys.BANK_ID_LAST_ACTION,
            JSON.stringify({
                redirect: AMLRedirect.ContributionWithQuestionare,
                contractId,
                contractType: contractType as PensionContractType,
            }),
        );
    }, [contractId, contractType]);

    if (
        readEnv("GATSBY_FEATURE_IS_NEW_DPS_CONTRIBUTION_CHANGE_ENABLED") === "true" &&
        contractType === ProductType.Uf
    ) {
        return (
            <ChangePensionContribution
                disabled={props.disabled}
                kind="homePage"
                icon={<Icons.Suitcase sx={{ width: "2.4rem", height: "2.4rem" }} />}
                bankIdAuthorizationUri={getBankIdAuthorizationUri()}
                contractId={contractId}
                contractType={contractType as PensionContractType}
                beforeBankIdAuthorizationAction={handleBeforeBankIdAuthorization}
                scrollToEmployerSection
                buttonText={t("SmartActionListType.EmployerContribution")}
            />
        );
    }

    return (
        <SmartAction
            {...props}
            action="EmployerContribution"
            icon={<Icons.Suitcase sx={{ width: "2rem", height: "2rem" }} />}
        >
            {t("SmartActionListType.EmployerContribution")}
        </SmartAction>
    );
};

export const Coverage: FC<ISmartAction> = (props) => {
    const { t } = useTypedTranslation();
    return (
        <SmartAction {...props} action="Coverage" icon={<Icons.Insurance sx={{ width: "2.4rem", height: "2.4rem" }} />}>
            {t("SmartActionListType.Coverage")}
        </SmartAction>
    );
};

export const CoverageFufi: FC<ISmartAction> = (props) => {
    const { t } = useTypedTranslation();
    return (
        <SmartAction {...props} action="Coverage" icon={<Icons.Insurance sx={{ width: "2.4rem", height: "2.4rem" }} />}>
            {t("SmartActionListType.CoverageFufi")}
        </SmartAction>
    );
};

export const Inflation: FC<ISmartAction> = (props) => {
    const { t } = useTypedTranslation();
    return (
        <SmartAction
            {...props}
            action="Inflation"
            icon={<Icons.InflationIncrease sx={{ width: "2.4rem", height: "2.4rem" }} />}
        >
            {t("SmartActionListType.Inflation")}
        </SmartAction>
    );
};

export const ChangeAdress: FC<ISmartAction> = (props) => {
    const { t } = useTypedTranslation();
    return (
        <SmartAction
            {...props}
            action="ChangeAdress"
            icon={<Icons.Address sx={{ width: "1.7rem", height: "2.2 rem" }} />}
        >
            {t("SmartActionListType.ChangeAdress")}
        </SmartAction>
    );
};

export const NewQuestionnaireButton: FC<ButtonProps> = (props) => {
    const { contractId: id } = useContractId();
    const { contractType } = useContractTypeContext();

    return (
        <PensionQuestionnaireStepperButton
            buttonProps={props}
            type="homePage"
            contractId={id}
            contractType={contractType}
        />
    );
};

export const SmartActionSkeleton: FC<ISmartAction> = () => {
    return (
        <Skeleton
            variant="rounded"
            sx={{
                width: "33.33%",
                height: { md: 92, xs: 74 },
                bgcolor: "primary.main",
            }}
        />
    );
};

export const SmartActionComponents = {
    Changes,
    InvestmentStrategy,
    NextPayment,
    Payments,
    Report,
    Fond,
    TaxCertificate,
    EmployerContribution,
    Coverage,
    CoverageFufi,
    ChangeAdress,
    SmartActionSkeleton,
    Inflation,
    NewQuestionnaireButton,
} as const;
